/**
 * Help content registry.
 *
 * Key format = router pathname (or pathname prefix). Lookup tries exact match
 * first, then progressively shorter prefixes (e.g. '/invoices/123' → '/invoices').
 *
 * Edit freely. Each entry supports:
 *   - title:    Heading inside the help dialog
 *   - tagline:  One-line summary under the title
 *   - what:     'What is this?' — paragraph(s)
 *   - how:      Ordered list of steps
 *   - tips:     Bullets with shortcuts / pitfalls / pro-tips
 *   - useCases: Array of practical scenarios (optional)
 *   - related:  Array of {label, path} to send the user elsewhere (optional)
 */
const helpContent = {
  '/import': {
    title: 'Importar datos',
    tagline: 'Excel/CSV en lote + PDFs con OCR.',
    what: 'Sube archivos Excel o CSV para crear clientes, servicios, proveedores, gastos o facturas de golpe. Si tienes PDFs/tickets, te enviamos a la Bandeja OCR que extrae los datos automáticamente con IA.',
    how: [
      'Elige la entidad que quieres importar.',
      'Descarga la plantilla Excel (opcional pero recomendado).',
      'Arrastra tu archivo. Detectamos automáticamente las columnas.',
      'Revisa el mapeo y confirma. Saltamos duplicados por NIF/número factura.',
    ],
    tips: [
      'Excel exportado de Holded, Quipu o FacturaDirecta suele funcionar tal cual.',
      'Para facturas, si el cliente no existe se crea automáticamente.',
      'Los duplicados (mismo NIF o número de factura) se saltan por defecto.',
      'Para PDFs e imágenes usa la Bandeja OCR (extrae con IA).',
    ],
  },
  '/dashboard': {
    title: 'Dashboard',
    tagline: 'Tu radar diario del negocio.',
    what: 'Vista resumen con los KPIs clave: facturado, pendiente de cobro, vencido, balance del año y gráfico mensual. También resalta alertas: facturas vencidas, proyectos en riesgo de pérdida y próximos vencimientos fiscales.',
    how: [
      'Revísalo cada mañana para detectar tareas urgentes.',
      'Pulsa cualquier alerta (factura vencida, proyecto en pérdida, impuesto próximo) para ir directo a su detalle.',
      'Los KPIs reflejan el año fiscal en curso.',
    ],
    tips: [
      'Las facturas vencidas son las que ya superaron la fecha de vencimiento sin cobrarse.',
      'El widget "Proyectos en riesgo" combina margen negativo, presupuesto sobrepasado y horas no facturadas.',
      'Si ves números a 0, probablemente aún no has emitido facturas este año.',
    ],
  },
  '/invoices': {
    title: 'Facturas',
    tagline: 'Emite, envía y cobra tus facturas.',
    what: 'Listado completo de tus facturas emitidas con filtros por estado (borrador, pendiente, pagada, vencida). Desde aquí creas, descargas PDF, envías al cliente y registras cobros.',
    how: [
      'Pulsa "Nueva factura" para crear una desde cero, o "Desde presupuesto" para convertir un presupuesto aceptado.',
      'En cada factura puedes: editar (si está en borrador), descargar PDF, copiar enlace de pago, enviar al portal del cliente o marcar como pagada.',
      'Los números se generan automáticamente con el prefijo configurado en Ajustes.',
    ],
    tips: [
      'Activa Verifactu en Ajustes para incluir QR + hash en el PDF (obligatorio AEAT a partir de 2026).',
      'Si trabajas con horas, factúralas desde Proyectos → Horas con un solo clic.',
      'Una factura pagada no se puede editar — solo anular y emitir rectificativa.',
    ],
    useCases: [
      'Cliente confirma presupuesto → conviertes a factura en 1 clic.',
      'Cobro en efectivo → marcas como pagada manualmente.',
      'Cliente pide pagar online → copias el enlace de pago Stripe.',
    ],
  },
  '/proformas': {
    title: 'Proformas',
    tagline: 'Facturas proforma (documento no fiscal previo a la venta).',
    what: 'Una proforma es una propuesta comercial con aspecto de factura que envías al cliente ANTES de cerrar la operación. NO cuenta para Hacienda y NO entra en Verifactu.',
    how: [
      'Crea la proforma y envíala al cliente como PDF (lleva sello "DOCUMENTO NO FISCAL").',
      'Cuando el cliente confirme, pulsa "Convertir a factura": se genera un borrador de factura con los mismos datos para que la emitas.',
      'Tiene su propia serie (PRO{año}) independiente de las facturas reales.',
    ],
    tips: [
      'Útil para licitaciones, anticipos, exportaciones y operaciones internacionales que requieren documento previo.',
      'Una vez convertida en factura, la proforma queda bloqueada como referencia.',
    ],
    related: ['/invoices', '/quotes'],
  },

  '/quotes': {
    title: 'Presupuestos',
    tagline: 'Propuestas que se convierten en factura con un clic.',
    what: 'Crea presupuestos para tus clientes, envíalos al portal y, cuando los acepten, conviértelos en factura sin reescribir nada.',
    how: [
      'Pulsa "Nuevo presupuesto", añade cliente, items y caducidad.',
      'Envía el enlace al portal del cliente o el PDF por email.',
      'Cuando lo acepten, abre el presupuesto → "Convertir a factura".',
    ],
    tips: [
      'Configura la fecha de caducidad para crear urgencia.',
      'En el portal del cliente, las aceptaciones quedan firmadas con IP + timestamp.',
    ],
  },
  '/delivery-notes': {
    title: 'Albaranes',
    tagline: 'Entregas que se facturan después.',
    what: 'Documenta entregas de mercancía o servicios sin facturar todavía. Útil cuando facturas mensualmente todo lo entregado.',
    how: [
      'Crea albaranes según vayas entregando.',
      'A fin de mes, agrupa varios albaranes de un cliente en una sola factura.',
    ],
    tips: ['El albarán no genera asiento contable — la factura sí.'],
  },
  '/clients': {
    title: 'Clientes',
    tagline: 'Tu base de datos de clientes y CRM ligero.',
    what: 'Toda la información fiscal y de contacto de tus clientes. Cada cliente tiene su historial de facturas, presupuestos, mensajes del portal y rentabilidad.',
    how: [
      'Añade un cliente con NIF/CIF, dirección, email y tarifa por hora opcional.',
      'En el detalle ves todo lo facturado, cobros pendientes y mensajes del portal.',
      '"Invitar al portal" envía un enlace mágico para que el cliente vea sus documentos.',
    ],
    tips: [
      'Si añades hourly_rate al cliente, esa tarifa prevalece sobre la de la empresa al registrar horas.',
      'NIF/CIF se valida automáticamente al guardar (formato español).',
    ],
  },
  '/services': {
    title: 'Servicios',
    tagline: 'Catálogo reutilizable de lo que vendes.',
    what: 'Define los servicios/productos que ofreces con precio unitario, tipo de unidad e IVA. Al crear una factura, los añades con autocompletado.',
    how: [
      'Crea un servicio con nombre, precio, unidad (hora, unidad, proyecto) e IVA.',
      'Al emitir una factura, escribe el nombre y se rellena automáticamente.',
    ],
    tips: ['Si la unidad es "hora", el servicio aparecerá en el desplegable del cronómetro de Proyectos.'],
  },
  '/providers': {
    title: 'Proveedores',
    tagline: 'Quién te emite a ti facturas de gasto.',
    what: 'Registro de proveedores (alquileres, software, profesionales, etc.) para asociar a tus gastos. Útil para los modelos de retenciones (111 y 115).',
    how: ['Crea proveedores con NIF y categoría.', 'Al añadir un gasto, selecciónalo del desplegable.'],
    tips: ['Proveedores categorizados como "alquileres" alimentan automáticamente el modelo 115.'],
  },
  '/expenses': {
    title: 'Gastos',
    tagline: 'Tus gastos deducibles bien clasificados.',
    what: 'Registra cada gasto con su categoría, IVA soportado y proyecto/cliente al que se imputa. Cuanto mejor categorizado, mejor se deduce.',
    how: [
      'Pulsa "Nuevo gasto", indica proveedor, importe, IVA y categoría.',
      'Sube el ticket o factura como imagen/PDF.',
      'Si es recurrente (alquiler, software), márcalo para no olvidarlo.',
    ],
    tips: [
      'Para gastos con factura física, usa la Bandeja OCR — extrae los datos automáticamente.',
      'Categorías "servicios_profesionales" alimentan el modelo 111.',
      'Categorías "alquiler" alimentan el modelo 115.',
    ],
  },
  '/expenses/inbox': {
    title: 'Bandeja OCR',
    tagline: 'Sube tickets y la IA hace el resto.',
    what: 'Sube fotos o PDFs de facturas/tickets recibidos. La IA (Claude Vision) extrae proveedor, base, IVA, total y fecha. Tú revisas y confirmas.',
    how: [
      'Arrastra varios archivos a la zona de subida o pulsa para seleccionar.',
      'La IA procesa cada uno (10-15s por documento).',
      'Revisa los datos extraídos y pulsa "Convertir en gasto" para guardarlo.',
    ],
    tips: [
      'Detecta duplicados antes de crear el gasto.',
      'Si la confianza es baja, los campos críticos aparecen en amarillo.',
      'Puedes editar cualquier campo antes de confirmar.',
    ],
  },
  '/contracts': {
    title: 'Contratos con IA',
    tagline: 'Genera contratos profesionales en lenguaje natural.',
    what: 'Describe en español lo que necesitas ("contrato de servicios de diseño para X por 6 meses, 3000€/mes") y la IA redacta un contrato completo basado en plantillas legales españolas.',
    how: [
      'Pulsa "Nuevo contrato" y elige una plantilla base.',
      'Rellena las variables (partes, importe, duración).',
      'Genera, revisa, edita y descarga PDF.',
    ],
    tips: ['Los contratos generados son orientativos — siempre revisa con tu asesor o abogado antes de firmar.'],
  },
  '/calendar': {
    title: 'Calendario',
    tagline: 'Tu agenda fiscal y comercial unificada.',
    what: 'Vista mensual con: vencimientos de facturas, presupuestos caducos, próximos modelos fiscales (303, 130, 111, 115), pagos de suscripciones y eventos manuales.',
    how: ['Cambia de mes con las flechas.', 'Pulsa un evento para ir a su detalle.'],
    tips: ['Los días con varios eventos muestran un punto por tipo (color codificado).'],
  },
  '/projects': {
    title: 'Proyectos & Horas',
    tagline: 'Time tracking + rentabilidad real.',
    what: 'Crea proyectos por cliente con presupuesto y tarifa, registra tus horas (manual o con cronómetro), facturalas con un clic y mide el margen real (ingresos − gastos − coste de tu tiempo).',
    how: [
      'Crea un proyecto: cliente, tipo (por hora o precio fijo), tarifa y presupuesto.',
      'Usa el cronómetro flotante (esquina inferior derecha) para arrancar/parar tiempos.',
      'En el tab "Horas", selecciona registros y pulsa "Crear factura" → agrupa por proyecto, día o una sola línea.',
      'El tab "Rentabilidad" muestra dónde ganas y dónde pierdes.',
    ],
    tips: [
      'Define tu Coste interno/h en Ajustes → empresa para que el margen sea realista.',
      'La tarifa cascada: project → service → client → company → 0.',
      'No puedes editar registros ya facturados — solo eliminarlos invalidaría la factura.',
    ],
    useCases: [
      'Freelance que cobra por horas: cronómetro mientras trabaja, factura mensual con 1 clic.',
      'Agencia: medir qué clientes son rentables y cuáles consumen más horas de las cobradas.',
    ],
  },
  '/banking': {
    title: 'Banca (CSV)',
    tagline: 'Conciliación importando extractos.',
    what: 'Sube el extracto bancario en CSV y la app lo concilia con tus facturas pendientes de cobro y tus gastos no asociados.',
    how: ['Descarga el CSV de tu banca online.', 'Súbelo y revisa sugerencias de match (concepto + importe).', 'Confirma o ignora cada propuesta.'],
    tips: ['Banca PSD2 (módulo siguiente) automatiza esto sin subir CSVs.'],
  },
  '/banking-psd2': {
    title: 'Banca PSD2 (Open Banking)',
    tagline: 'Conecta tu banco directamente. Mockup para pruebas.',
    what: 'Sincronización automática con cuentas españolas vía agregadores PSD2 (GoCardless, Tink, Plaid…). En esta versión está mockeado con datos de demo para que pruebes la UX.',
    how: ['Conecta una cuenta de demo.', 'La app trae los movimientos automáticamente.', 'Concilia con facturas/gastos como en banca CSV.'],
    tips: ['Para producción real necesitas activar credenciales del agregador.'],
  },
  '/cashflow': {
    title: 'Cash Flow',
    tagline: 'Predice tu caja a 6 meses vista.',
    what: 'Proyección de tesorería combinando: cobros previstos de facturas pendientes, pagos a proveedores, suscripciones, vencimientos fiscales y escenarios "what-if".',
    how: ['Revisa el gráfico de saldo acumulado mes a mes.', 'Crea escenarios alternativos (ej: "y si pierdo el cliente X").', 'Las alertas señalan meses en rojo.'],
    tips: ['Combina con Predicción Fiscal para añadir el impacto de impuestos al cálculo.'],
  },
  '/receivables': {
    title: 'Por Cobrar (Dunning)',
    tagline: 'Persigue tus impagos automáticamente.',
    what: 'Listado de facturas pendientes/vencidas con un sistema de recordatorios automáticos en 5 pasos (T-7, día del vencimiento, +7, +14, +30).',
    how: [
      'Configura las 5 plantillas de email en el editor.',
      'Activa la cadencia en cada cliente o globalmente.',
      'El tick automático recorre las facturas y envía los recordatorios que toquen.',
    ],
    tips: ['Para producción, conecta un proveedor de email real (Resend) en Ajustes.'],
  },
  '/tax': {
    title: 'Impuestos',
    tagline: '8 modelos AEAT + libros fiscales.',
    what: 'Genera y descarga modelos AEAT (303, 130, 111, 115, 349, 390, 100, IS), libros de facturas emitidas/recibidas y el aging report.',
    how: [
      'Selecciona año + trimestre y pulsa el modelo que necesites.',
      'Revisa las casillas calculadas y ajusta manualmente si hace falta.',
      'Descarga PDF/XLSX o presenta por canal directo (próximamente).',
    ],
    tips: ['Cada cifra es trazable: pulsa para ver qué facturas/gastos la componen.'],
  },
  '/tax/forecast': {
    title: 'Predicción Fiscal',
    tagline: 'Lo que vas a pagar — hoy y al cierre.',
    what: 'Extrapola tu actividad actual y predice cuánto pagarás en cada modelo (303, 130, 111, 115) y el IRPF/IS anual aplicando tramos progresivos. Incluye simulador "what-if".',
    how: [
      'En el dashboard ves los próximos vencimientos con devengado vs proyectado al cierre.',
      'Pulsa una tarjeta para ver el desglose (qué facturas/gastos lo alimentan).',
      'Usa el Simulador para ver el delta si emites/recibes una factura concreta.',
    ],
    tips: [
      'Las estimaciones son conservadoras — pueden sobrestimar lo que pagarás.',
      'Define tu régimen fiscal en Ajustes → Impuestos para que aplique correctamente.',
      'Los insights te avisan si vas a saltar de tramo IRPF o si tendrás IVA a compensar.',
    ],
  },
  '/ai-advisor': {
    title: 'Asesor IA (Claude)',
    tagline: 'Tu copiloto financiero que conoce tus datos.',
    what: 'Chat con IA (Claude Sonnet 4.6) que lee en tiempo real tu negocio: ingresos, gastos, IVA, IRPF, facturas pendientes, proyectos. Responde con números reales y sugiere acciones.',
    how: [
      'Pulsa un prompt sugerido o escribe tu propia pregunta.',
      'Las conversaciones se guardan en el historial (sidebar izquierda).',
      'Pregunta cosas concretas: "Si compro un MacBook de 3000€ ¿cuánto me ahorro en IRPF?".',
    ],
    tips: [
      'Cuanto más concreta la pregunta, mejor la respuesta.',
      'Puede sugerirte rutas internas para ejecutar la acción.',
      'No es asesoramiento fiscal vinculante — contrasta con tu asesor.',
    ],
    useCases: [
      '"¿Cómo está mi negocio este mes?"',
      '"¿Voy a saltar al siguiente tramo IRPF?"',
      '"¿Qué cliente es más rentable?"',
    ],
  },
  '/auxiliar': {
    title: 'Auxiliar IA',
    tagline: 'Acciones rápidas por voz o texto.',
    what: 'Asistente de acciones simples: "crea factura para Cliente X de 500€", "muéstrame los gastos del mes". Habla o escribe y la IA ejecuta la acción.',
    how: ['Pulsa el botón del micrófono o escribe.', 'Confirma la acción propuesta antes de aplicarla.'],
    tips: ['Para análisis o consejos, usa el Asesor IA en su lugar.'],
  },
  '/subscriptions': {
    title: 'Suscripciones',
    tagline: 'Facturación recurrente + SEPA.',
    what: 'Gestiona suscripciones a clientes con 8 frecuencias (mensual, trimestral, anual…), genera facturas automáticamente y prepara lotes SEPA pain.008 para domiciliar cobros.',
    how: [
      'Crea una suscripción: cliente, items, frecuencia y método de pago.',
      'Si es SEPA, primero genera el mandato (UMR) con el IBAN validado.',
      'El motor automático crea facturas en cada vencimiento.',
      'Genera el lote SEPA XML y súbelo a tu banca.',
    ],
    tips: [
      'MRR y ARR se calculan normalizando todas las frecuencias a mensual.',
      'Pausar al final del periodo deja la última factura ya emitida.',
      'IBAN se valida con mod-97 ISO 13616 — checksums inválidos se rechazan.',
    ],
  },
  '/settings': {
    title: 'Ajustes',
    tagline: 'Configuración general de tu empresa.',
    what: 'Datos de empresa, IBAN, logo, colores, pie de factura, numeración, Verifactu, tarifas para time tracking, y configuración del portal del cliente.',
    how: [
      'Pestaña Empresa: tus datos fiscales y de marca aparecen en cada factura.',
      'Pestaña Time Tracking: configura tarifa por hora y coste interno.',
      'Pestaña Portal: color y mensaje de bienvenida del portal del cliente.',
    ],
    tips: [
      'El Coste interno/h es clave para que la rentabilidad de proyectos sea real.',
      'El color de marca tiñe los headers de tablas en los PDFs.',
    ],
  },
  '/reports': {
    title: 'Informes',
    tagline: 'Análisis avanzado del negocio.',
    what: 'Gráficos y tablas con: facturación mensual, top clientes, top servicios, gastos por categoría y comparativas año vs año.',
    how: ['Cambia el rango de fechas con el selector.', 'Descarga en PDF/XLSX para tu asesor.'],
    tips: ['Para rentabilidad por cliente/proyecto, usa Proyectos → Rentabilidad.'],
  },
  '/history': {
    title: 'Historial',
    tagline: 'Registro de actividad y cambios.',
    what: 'Auditoría cronológica de eventos: facturas creadas/pagadas, gastos añadidos, contratos generados, etc.',
    how: ['Filtra por tipo de evento o por usuario.'],
    tips: ['Útil para depurar discrepancias o demostrar trazabilidad ante AEAT.'],
  },
  '/help': {
    title: 'Ayuda',
    tagline: 'Guías, FAQs y soporte.',
    what: 'Centro de ayuda con tutoriales y respuestas a las dudas más frecuentes.',
    how: ['Busca por palabra clave o navega por categorías.'],
    tips: ['Si no encuentras lo que buscas, contacta soporte desde Ajustes.'],
  },
  '/admin/demo-requests': {
    title: 'Solicitudes Demo',
    tagline: 'Bandeja de leads desde la landing pública.',
    what: 'Cada vez que un visitante rellena el formulario "Solicitar Demo" en facturcal.com, su solicitud aparece aquí. Puedes ver sus datos, marcar el estado del seguimiento y guardar notas internas privadas.',
    how: [
      'Filtra por estado: nuevas, contactadas, programadas, convertidas o descartadas.',
      'Selecciona una solicitud para ver el detalle completo y cambiar su estado.',
      'Guarda notas internas con cada interacción (no se envían al lead).',
      'Marca "Convertida" cuando el lead se transforme en cliente de pago.',
    ],
    tips: [
      'El número rojo junto a "Solicitudes Demo" en el sidebar son leads sin contactar.',
      'El email del lead es un mailto: directo y el teléfono es tel: para llamada con un clic.',
      'Borra solicitudes spam con la papelera del detalle.',
    ],
    use_cases: [
      'Equipo comercial gestionando un funnel de demos.',
      'Una persona puede llevar el seguimiento sin salir de Facturcal.',
    ],
    related: ['/admin/analytics'],
  },
  '/admin/analytics': {
    title: 'Analítica web',
    tagline: 'Tráfico de la landing y embudo de conversión.',
    what: 'Panel en vivo con visitantes, dispositivos, países y rutas más visitadas. Si activas el embudo, verás de Landing → Pricing → Demo solicitado.',
    how: [
      'Selecciona el rango de días en la cabecera.',
      'Mira las visitas activas en tiempo real (últimos 5 min).',
      'Analiza el top de páginas para detectar contenido potente.',
    ],
    tips: [
      'Combínalo con las solicitudes de Demo para medir conversión real.',
    ],
  },
};

/**
 * Resolve the help entry for a given pathname.
 * Tries exact match first, then progressively shorter prefixes.
 */
export function getHelpFor(pathname) {
  if (!pathname) return null;
  if (helpContent[pathname]) return helpContent[pathname];
  const parts = pathname.split('/').filter(Boolean);
  for (let i = parts.length; i > 0; i--) {
    const candidate = '/' + parts.slice(0, i).join('/');
    if (helpContent[candidate]) return helpContent[candidate];
  }
  return null;
}

export default helpContent;
